import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Printer, ShieldCheck } from 'lucide-react';
import { Visit, VisitDetail } from '@cloudsign/shared';
import { ApiClientError, api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { errorText, t } from '../../lib/i18n';
import { useCan, useCaps } from '../../lib/session';
import { Alert, Badge, Button, ConfirmButton, Spinner, TextField } from '../../components/ui';
import { useToast } from '../../components/Toast';
import { isoToLocal, localToIso } from './format';
import { VisitAgreements } from '../agreements';
import { RaisePermitDrawer } from '../permits/RaisePermitDrawer';
import { IdCheckResultSection } from './IdCheckResult';
import { FlagBadges, MethodIcon, Overlay, PersonAvatar } from './parts';

const message = (e: unknown): string => (e instanceof ApiClientError ? errorText(e.body) : t('error.internal'));

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-3 text-sm">
      <dt className="text-slate-600">{label}</dt>
      <dd className="col-span-2 text-slate-900">{children}</dd>
    </div>
  );
}

function Edits({ visit, timeZone, canTimes, canNotes, onChanged }: { visit: VisitDetail; timeZone: string; canTimes: boolean; canNotes: boolean; onChanged: () => void }) {
  const toast = useToast();
  const [inAt, setInAt] = useState(isoToLocal(visit.signedInAt, timeZone));
  const [outAt, setOutAt] = useState(visit.signedOutAt ? isoToLocal(visit.signedOutAt, timeZone) : '');
  const [notes, setNotes] = useState(visit.notes);
  const [error, setError] = useState<string | null>(null);
  const times = useMutation({
    mutationFn: () =>
      api.patch(`/visits/${visit.id}`, {
        body: { signedInAt: localToIso(inAt, timeZone), signedOutAt: outAt ? localToIso(outAt, timeZone) : null },
        schema: Visit,
      }),
    onSuccess: () => {
      setError(null);
      toast(t('common.saved'));
      onChanged();
    },
    onError: (e) => setError(message(e)),
  });
  const saveNotes = useMutation({
    mutationFn: () => api.put(`/visits/${visit.id}/notes`, { body: { notes }, schema: Visit }),
    onSuccess: () => {
      setError(null);
      toast(t('common.saved'));
      onChanged();
    },
    onError: (e) => setError(message(e)),
  });
  return (
    <>
      {error ? <Alert>{error}</Alert> : null}
      {canTimes ? (
        <section aria-labelledby="drawer-times" className="space-y-3">
          <h3 id="drawer-times" className="text-sm font-semibold text-slate-900">{t('today.drawer.editTimes')}</h3>
          <TextField label={t('today.drawer.signedIn')} type="datetime-local" value={inAt} onChange={(e) => setInAt(e.target.value)} />
          <TextField label={t('today.drawer.signedOut')} type="datetime-local" value={outAt} onChange={(e) => setOutAt(e.target.value)} />
          <Button disabled={times.isPending} onClick={() => times.mutate()}>{t('today.drawer.saveTimes')}</Button>
        </section>
      ) : null}
      <section aria-labelledby="drawer-notes" className="space-y-2">
        <h3 id="drawer-notes" className="text-sm font-semibold text-slate-900">{t('today.drawer.notes')}</h3>
        {canNotes ? (
          <>
            <label className="sr-only" htmlFor="drawer-notes-input">{t('today.drawer.notes')}</label>
            <textarea id="drawer-notes-input" rows={3} maxLength={2000} className="block w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm" value={notes} onChange={(e) => setNotes(e.target.value)} />
            <Button disabled={saveNotes.isPending || notes === visit.notes} onClick={() => saveNotes.mutate()}>{t('today.drawer.saveNotes')}</Button>
          </>
        ) : (
          <p className="whitespace-pre-wrap text-sm text-slate-800">{visit.notes || t('today.drawer.noNotes')}</p>
        )}
      </section>
    </>
  );
}

/** The visit drawer: everything about one visit, with the actions the person is allowed. */
export function VisitDrawer({ visitId, onClose, onChanged }: { visitId: string; onClose: () => void; onChanged: () => void }) {
  const caps = useCaps();
  const qc = useQueryClient();
  const toast = useToast();
  const canEditTimes = useCan('visitors.edit_visits');
  const canEditNotes = useCan('visitors.edit_notes');
  const canProtect = useCan('history.protect');
  const canDelete = useCan('history.delete');
  const canRaisePermit = useCan('permits.raise');
  const permitsOn = canRaisePermit && caps.modules.some((m) => m.key === 'permits' && m.enabled);
  const [raising, setRaising] = useState(false);
  const badgesOn = caps.modules.some((m) => m.key === 'badges' && m.enabled);
  const agreementsOn = caps.modules.some((m) => m.key === 'agreements' && m.enabled);
  const [error, setError] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['today', 'visit', visitId],
    queryFn: () => api.get(`/visits/${visitId}`, { schema: VisitDetail }),
  });
  const zoneQ = caps.tenant?.timeZone ?? 'Europe/London';
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['today', 'visit', visitId] });
    onChanged();
  };
  const protect = useMutation({
    mutationFn: (on: boolean) => api.put(`/visits/${visitId}/protected`, { body: { protected: on }, schema: Visit }),
    onSuccess: refresh,
    onError: (e) => setError(message(e)),
  });
  const del = useMutation({
    mutationFn: () => api.delete(`/visits/${visitId}`),
    onSuccess: () => {
      toast(t('today.drawer.deleted'));
      onChanged();
      onClose();
    },
    onError: (e) => setError(message(e)),
  });

  const v = q.data;
  return (
    <Overlay drawer title={v ? v.name : t('today.drawer.title')} onClose={onClose}>
      {q.isPending ? <Spinner /> : null}
      {q.isError ? <Alert>{message(q.error)}</Alert> : null}
      {error ? <Alert>{error}</Alert> : null}
      {v ? (
        <>
          <div className="flex items-center gap-4">
            <PersonAvatar name={v.name} fileId={v.photoThumbFileId} size="h-16 w-16 text-lg" />
            <div className="min-w-0">
              <p className="truncate text-lg font-semibold text-slate-900">{v.name}</p>
              <p className="text-sm text-slate-600">
                {v.groupName} · {v.siteName}
              </p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                <Badge tone={v.signedOutAt ? 'neutral' : 'good'}>{v.signedOutAt ? t('today.status.out') : t('today.status.in')}</Badge>
                <FlagBadges flags={v.flags} />
                {v.protected ? <Badge tone="warn">{t('today.drawer.protected')}</Badge> : null}
              </div>
            </div>
          </div>

          <dl className="space-y-2">
            <Row label={t('today.drawer.signedIn')}>
              <span className="inline-flex items-center gap-2">
                {formatDateTime(v.signedInAt, zoneQ)}
                <MethodIcon method={v.signInMethod} device={v.signInDeviceName} />
              </span>
            </Row>
            <Row label={t('today.drawer.signedOut')}>
              {v.signedOutAt ? (
                <span className="inline-flex items-center gap-2">
                  {formatDateTime(v.signedOutAt, zoneQ)}
                  {v.signOutMethod ? <MethodIcon method={v.signOutMethod} /> : null}
                </span>
              ) : (
                t('today.drawer.stillIn')
              )}
            </Row>
            {v.hostName ? <Row label={t('today.col.host')}>{v.hostName}</Row> : null}
          </dl>

          <section aria-labelledby="drawer-answers" className="space-y-2">
            <h3 id="drawer-answers" className="text-sm font-semibold text-slate-900">{t('today.drawer.answers')}</h3>
            {v.answers.length === 0 ? (
              <p className="text-sm text-slate-600">{t('today.drawer.noAnswers')}</p>
            ) : (
              <dl className="space-y-2">
                {v.answers.map((a) => (
                  <Row key={`${a.fieldId}-${a.stage}`} label={a.label}>
                    {a.redacted ? <span className="italic text-slate-600">{t('today.drawer.redacted')}</span> : (a.text ?? '')}
                  </Row>
                ))}
              </dl>
            )}
          </section>

          <section aria-labelledby="drawer-notices" className="space-y-2">
            <h3 id="drawer-notices" className="text-sm font-semibold text-slate-900">{t('today.drawer.notices')}</h3>
            {v.acceptances.length === 0 ? (
              <p className="text-sm text-slate-600">{t('today.drawer.noNotices')}</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {v.acceptances.map((n) => (
                  <li key={n.id} className="flex items-center gap-2">
                    <ShieldCheck aria-hidden="true" className="h-4 w-4 text-green-700" />
                    {t('today.drawer.noticeLine', { title: n.title, version: n.version, when: formatDateTime(n.acceptedAt, zoneQ) })}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {agreementsOn ? <VisitAgreements visitId={v.id} /> : null}

          <IdCheckResultSection visitId={v.id} timeZone={zoneQ} />

          <section aria-labelledby="drawer-history" className="space-y-2">
            <h3 id="drawer-history" className="text-sm font-semibold text-slate-900">{t('today.drawer.history')}</h3>
            <ol className="space-y-1.5 text-sm">
              {v.events.map((e) => (
                <li key={e.id} className="flex flex-wrap items-center gap-x-2 text-slate-800">
                  <span>{t(`today.event.${e.kind}`)}</span>
                  <span className="text-slate-600">{formatDateTime(e.occurredAt, zoneQ)}</span>
                  <MethodIcon method={e.method} device={e.deviceName} />
                  {e.actorLabel ? <span className="text-slate-600">{e.actorLabel}</span> : null}
                  {!e.applied ? <Badge tone="warn">{e.noteKey ? t(e.noteKey) : t('today.event.notApplied')}</Badge> : null}
                </li>
              ))}
            </ol>
          </section>

          <Edits key={v.updatedAt} visit={v} timeZone={zoneQ} canTimes={canEditTimes} canNotes={canEditNotes} onChanged={refresh} />

          <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-4">
            {badgesOn ? (
              <Button onClick={() => window.open(`/api/v1/badges/visits/${v.id}/pdf`, '_blank', 'noopener')}>
                <Printer aria-hidden="true" className="h-4 w-4" />
                {t('today.drawer.printBadge')}
              </Button>
            ) : null}
            {permitsOn && !v.signedOutAt ? <Button onClick={() => setRaising(true)}>{t('w23.permit.raise')}</Button> : null}
            {canProtect ? (
              <Button disabled={protect.isPending} onClick={() => protect.mutate(!v.protected)}>
                {v.protected ? t('today.drawer.unprotect') : t('today.drawer.protect')}
              </Button>
            ) : null}
            {canDelete ? (
              <ConfirmButton label={t('today.drawer.delete')} disabled={v.protected} pending={del.isPending} onConfirm={() => del.mutate()} />
            ) : null}
          </div>
          {raising ? (
            <RaisePermitDrawer siteId={v.siteId} visitId={v.id} memberId={v.memberId} holderName={v.name} onClose={() => setRaising(false)} onRaised={refresh} />
          ) : null}
        </>
      ) : null}
    </Overlay>
  );
}
